import { mapObjectKeysToValues } from '../../utils/core/object';

// Re-export types from business/incident for convenience
export type { IncidentLodgementFormConfiguration } from '../../types/business/incident';

export const INCIDENT_MILESTONES = {
    identified: {
        internalName: 'date_identified',
        label: 'Date identified',
    },
    occurred: {
        internalName: 'date_occurred',
        label: 'Date occurred',
    },
    reported: {
        internalName: 'date_reported',
        label: 'Date reported',
    },
    closed: {
        label: 'Date closed',
        internalName: 'date_closed',
    },
    reopened: {
        label: 'Date re-opened',
        internalName: 'date_reopened',
    },
} as const;

export const INCIDENT_STATISTIC_SETTINGS = {
    INCIDENTS_PER_USER_STEPS: [0, 2],
    INCIDENTS_RESULTED_IN_BREACH_PERCENTAGE_STEPS: [50, 70],
    INCIDENTS_OPEN_MORE_THAN_30_DAYS_PERCENTAGE_STEPS: [50, 70],
} as const;

export const INCIDENT_STATUSES = {
    open: 'open',
    closed: 'closed',
} as const;

export type IncidentStatus = (typeof INCIDENT_STATUSES)[keyof typeof INCIDENT_STATUSES];

export const INCIDENT_STATUSES_KEYS = mapObjectKeysToValues(INCIDENT_STATUSES);

export type IncidentEntity = 'complaint' | 'risk';

/**
 * Max length of an incident's description on creation. Enforced by the add form
 * (counter + maxLength) and the createIncident schema. Update paths are
 * deliberately uncapped — legacy records exceed this.
 */
export const INCIDENT_DESCRIPTION_MAX_LENGTH = 3000;

/**
 * Coverage-checklist keys, in the order shown under the incident description on the
 * create form ("What your description covers"). The `key`s are the stable
 * client/server contract for AI coverage suggestions and back the CUR-317
 * description-quality metric (the create form persists which of these the AI marked
 * covered — see `descriptionCoverage`).
 */
export const INCIDENT_COVERAGE_KEYS = ['what_happened', 'when_how_long', 'how_found', 'what_at_risk'] as const;

export type IncidentCoverageKey = (typeof INCIDENT_COVERAGE_KEYS)[number];

/** Chip label per coverage key, in display order. */
export const INCIDENT_COVERAGE_ITEMS = [
    { key: 'what_happened', label: 'What happened?' },
    { key: 'when_how_long', label: 'When and how long?' },
    { key: 'how_found', label: 'How was it found?' },
    { key: 'what_at_risk', label: 'What was at risk?' },
] as const satisfies ReadonlyArray<{ key: IncidentCoverageKey; label: string }>;
